import React from 'react'
import styled from 'styled-components'
import HomeWorkIcon from '@mui/icons-material/HomeWorkOutlined'
import GavelIcon from '@mui/icons-material/Gavel'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import EventNoteIcon from '@mui/icons-material/EventNoteOutlined'
import PeopleIcon from '@mui/icons-material/PeopleOutline'
import ApartmentIcon from '@mui/icons-material/ApartmentOutlined'
import RuleFolderIcon from '@mui/icons-material/RuleFolderOutlined'
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined'
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined'
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined'
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined'
import BuildIcon from '@mui/icons-material/BuildOutlined'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined'
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined'
import StickyNote2Icon from '@mui/icons-material/StickyNote2Outlined'
import EmailIcon from '@mui/icons-material/EmailOutlined'
import TimelineIcon from '@mui/icons-material/TimelineOutlined'
import SiteRisksIcon from '@mui/icons-material/GppMaybeOutlined'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'

const Aside = styled.aside`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.03);
  padding: 20px;
  align-self: flex-start;
  position: sticky;
  top: 16px;
  font-size: 13px;
`

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.11px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 12px;
`

const Item = styled.a`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  color: ${({ active }) => (active ? '#d24724' : '#333')};
  background: ${({ active }) => (active ? '#fbede9' : 'transparent')};
  font-weight: ${({ active }) => (active ? 500 : 400)};
  text-decoration: none;
  cursor: pointer;
  margin-bottom: 2px;
  transition: background 0.15s;

  &:hover {
    background: ${({ active }) => (active ? '#fbede9' : '#fafafa')};
  }

  svg {
    font-size: 16px !important;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${({ active }) => (active ? '#d24724' : '#999')};
  }
`

const ItemLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Count = styled.span`
  font-size: 11px;
  color: ${({ active, tone }) => (
    tone === 'crit' ? '#b91c1c'
    : tone === 'warn' ? '#c2410c'
    : active ? '#d24724'
    : '#999'
  )};
  background: ${({ active, tone }) => (
    tone === 'crit' ? '#fef2f2'
    : tone === 'warn' ? '#fff7ed'
    : active ? '#fff'
    : '#f2f2f2'
  )};
  padding: 1px 6px;
  border-radius: 9999px;
  font-weight: 500;
  flex-shrink: 0;
`

const Footer = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f2f2f2;
  font-size: 11px;
  color: #999;
  line-height: 1.5;
`

const SECTION_ICONS = {
  overview: InfoIcon,
  legislation: GavelIcon,
  activity: HistoryIcon,
  risk_compliance: ShieldIcon,
  building_info: HomeWorkIcon,
  planning_constraints: SiteRisksIcon,
  forecast: EventNoteIcon,
  people: PeopleIcon,
  units: ApartmentIcon,
  compliance: RuleFolderIcon,
  compliance_sets: FactCheckIcon,
  analytics_templates: InsertChartIcon,
  checklist_templates: InsertChartIcon,
  reports: AssessmentIcon,
  bp_audits: FactCheckIcon,
  building_assets: BuildIcon,
  documents: DescriptionIcon,
  action_items: PlaylistAddCheckIcon,
  tasks: AssignmentIcon,
  incidents: ReportProblemIcon,
  notes: StickyNote2Icon,
  emails: EmailIcon,
  recent_events: TimelineIcon,
  annotations: StickyNote2Icon,
  references: BookmarkBorderIcon,
}

const ALWAYS_SHOW_KEYS = new Set([
  'overview',
  'legislation',
  'activity',
  'risk_compliance',
  'building_info',
  'planning_constraints',
  'people',
  'compliance',
  'documents',
])

const countForSection = (section) => {
  if (!section.data) return null
  if (section.key === 'planning_constraints') return typeof section.data.summary?.flagged === 'number' ? section.data.summary.flagged : null
  if (typeof section.data.total === 'number') return section.data.total
  if (typeof section.data.uniqueTypes === 'number') return section.data.uniqueTypes
  if (Array.isArray(section.data.rows)) return section.data.rows.length
  if (Array.isArray(section.data.events)) return section.data.events.length
  if (Array.isArray(section.data.notes) && Array.isArray(section.data.emails)) {
    return section.data.notes.length + section.data.emails.length
  }
  return null
}

const formatCount = (n) => {
  if (typeof n !== 'number') return '—'
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`
  return String(n)
}

const toneFor = (section, count) => {
  if (section.key === 'action_items' && count > 0) return 'warn'
  if (section.key === 'incidents' && count > 0) return 'crit'
  if (section.key === 'forecast' && count > 0) return 'warn'
  if (section.key === 'planning_constraints' && count > 0) return (section.data?.summary?.critical > 0 ? 'crit' : 'warn')
  return null
}

const WikiSidebar = ({ sections, references, activeSectionKey, generatedAt, onSectionClick }) => {
  const visible = (sections || []).filter((s) => {
    if (ALWAYS_SHOW_KEYS.has(s.key)) return true
    const c = countForSection(s)
    return c !== null && c > 0
  })

  return (
    <Aside>
      <Label>Contents</Label>
      {visible.map((s) => {
        const count = countForSection(s)
        const Icon = SECTION_ICONS[s.key] || InfoIcon
        const tone = toneFor(s, count ?? 0)
        const active = activeSectionKey === s.key
        return (
          <Item
            key={s.key}
            href={`#section-${s.key}`}
            active={active}
            onClick={(e) => {
              e.preventDefault()
              onSectionClick?.(s.key)
            }}
          >
            <ItemLabel>
              <Icon />
              <span>{s.title}</span>
            </ItemLabel>
            <Count active={active} tone={tone}>{formatCount(count)}</Count>
          </Item>
        )
      })}
      {(references || []).length > 0 && (
        <Item
          href="#section-references"
          active={activeSectionKey === 'references'}
          onClick={(e) => {
            e.preventDefault()
            onSectionClick?.('references')
          }}
        >
          <ItemLabel>
            <BookmarkBorderIcon />
            <span>References</span>
          </ItemLabel>
          <Count active={activeSectionKey === 'references'}>{references.length}</Count>
        </Item>
      )}
      <Footer>
        {generatedAt ? (
          <>
            Last regenerated
            <br />
            <span style={{ color: '#333' }}>
              {new Date(generatedAt).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </>
        ) : (
          'Not yet generated'
        )}
      </Footer>
    </Aside>
  )
}

export default WikiSidebar
